/**
 * BOQ Payload Builder Utility
 *
 * Centralizes the creation of BOQ and BOQ version payloads to ensure consistency
 * across all API calls and reduce code duplication.
 */

import { ItemLibraryResponse } from "../api/itemLibrary";
import { retainingWallsBuildUpApi, RetainingWallsBuildUpSpecification } from "../api/retainingWallsBuildUp";
import { geoToberWallBuildUpApi, GeoToberWallBuildUpSpecification } from "../api/wallSectionBuildUp";
import { TenderState } from "../types/tender.types";
import { buildGroundworksItemLibrary } from "./groundworksItemLibraryBuilder";
import { computeGroundworksBoqSummary, computeGrandTotalFromSummary } from "./groundworksBoqSummaryComputer";
import { computeRoadsAndSewersBoqSummary } from "./roadsAndSewersBoqSummaryComputer";
import { prepareTenderForAPI } from "./storage";
import { calculateBoqTenderTotal } from "./boqTotals";
import { getNumericBoqVersion } from "./boqVersionPrefix";
import { serializeDynamicBillSummaryTabs, flattenCutFillsExtraItemsToBoqLines, flattenDrainageExtraItemsToBoqLines } from "./dynamicBillSummarySerialization";

export interface BoqPayloadOptions {
  state: TenderState;
  userName?: string;
  status?: string;
  boqStatus?: string;
  opportunityNo?: string;
  groundworksTotals: {
    grandTotal: number;          // BASE total before any adjustments
    tenderTotal?: number;        // FINAL total after all adjustments (prelims, OHP, discount)
    grandTotalWithAdjustments?: number; // Total with prelims/OHP but before discount
    discountPercent?: number;
    ohpPercent?: number;
    preliminariesPercent?: number;
    discountAmount?: number;
  };
  /** @deprecated no longer used — totals.tenderTotal is always calculateBoqTenderTotal(billOfQuantities[0]) */
  combinedTenderTotal?: number;
  settings?: {
    groundworks?: {
      overheadAndProfit?: number;
      preliminaries?: number;
      discount?: number;
    };
  };
  includeDiscountDisplay?: boolean;
  billOfQuantitiesData?: any;
  totalsData?: any;
  itemLibraryData?: ItemLibraryResponse;
  retainingWallsBuildUpSpecs?: RetainingWallsBuildUpSpecification[];
  geoToberWallsBuildUpSpecs?: GeoToberWallBuildUpSpecification[];
}

export interface VersionPayloadOptions {
  projectName: string;
  boqPayload: any;
  changeLog: string;
  version?: number | string;
}

/**
 * Creates a complete BOQ payload for API submission
 * This is the main payload structure used across the application
 */
export function createBoqPayload(options: BoqPayloadOptions) {
  const {
    state,
    userName,
    status,
    boqStatus,
    opportunityNo,
    groundworksTotals,
    settings,
    includeDiscountDisplay = false,
    billOfQuantitiesData,
    totalsData,
    itemLibraryData,
    retainingWallsBuildUpSpecs,
    geoToberWallsBuildUpSpecs,
  } = options;

  // Get base payload data from tender state
  const payloadData = prepareTenderForAPI(state, userName);

  // Resolve effective workflow status — boqStatus always takes precedence over status.
  // Both fields are kept in sync so the API receives identical values for both.
  const effectiveStatus = boqStatus || status;

  const billOfQuantities = billOfQuantitiesData || (() => {
    const groundworksSummary = computeGroundworksBoqSummary(state.groundworks, itemLibraryData, { retainingWalls: retainingWallsBuildUpSpecs, geoWalls: geoToberWallsBuildUpSpecs });
    // Use hook-provided values when available so the stored grandTotal matches
    // exactly what the UI displays. Fall back to summary-computed value only
    // when no hook values were passed (e.g. simple/legacy callers).
    const groundworksGrandTotal =
      groundworksTotals.tenderTotal ??
      groundworksTotals.grandTotalWithAdjustments ??
      computeGrandTotalFromSummary(groundworksSummary);
    // Don't recompute R&S grandTotal from this page — reuse the carried-across Sum
    // Analysis total (same value the R&S Review page's own save already maintains)
    // so combined-total projects still get an accurate figure here instead of a
    // stale/zeroed one. The per-domain `summary` line items below ARE recomputed
    // live here (mirrors groundworksSummary above) — same convention, independent
    // concern from the grandTotal figure.
    const roadsAndSewersGrandTotal = state.roadsAndSewersTotals?.grandTotal;
    const roadsAndSewersBaseSummary = computeRoadsAndSewersBoqSummary(state, itemLibraryData);
    const roadsAndSewersSummary = roadsAndSewersGrandTotal !== undefined
      ? {
          ...roadsAndSewersBaseSummary,
          // Ad-hoc "Add Section"/"Add Item" extra rows (useBillSummaryExtraItems.tsx,
          // reused from groundworks as-is) merge into each domain's own computed
          // array — same convention groundworks' own cutFills/foundations/etc merges
          // below use (flattenCutFillsExtraItemsToBoqLines is generic despite the
          // name; every R&S summary domain uses the same numeric BoqSummaryLineItem
          // shape, so no Drainage-style string-shape variant is needed here).
          prelims: [...roadsAndSewersBaseSummary.prelims, ...flattenCutFillsExtraItemsToBoqLines(state.prelimsExtraItems)],
          foulSewers: [...roadsAndSewersBaseSummary.foulSewers, ...flattenCutFillsExtraItemsToBoqLines(state.foulSewersExtraItems)],
          pumpStation: [...roadsAndSewersBaseSummary.pumpStation, ...flattenCutFillsExtraItemsToBoqLines(state.pumpStationExtraItems)],
          risingMain: [...roadsAndSewersBaseSummary.risingMain, ...flattenCutFillsExtraItemsToBoqLines(state.risingMainExtraItems)],
          stormSewers: [...roadsAndSewersBaseSummary.stormSewers, ...flattenCutFillsExtraItemsToBoqLines(state.stormSewersExtraItems)],
          pondsSwales: [...roadsAndSewersBaseSummary.pondsSwales, ...flattenCutFillsExtraItemsToBoqLines(state.pondsSwalesExtraItems)],
          cellularAttenuation: [...roadsAndSewersBaseSummary.cellularAttenuation, ...flattenCutFillsExtraItemsToBoqLines(state.cellularAttenuationExtraItems)],
          weholiteAttenuation: [...roadsAndSewersBaseSummary.weholiteAttenuation, ...flattenCutFillsExtraItemsToBoqLines(state.weholiteAttenuationExtraItems)],
          concAttenuation: [...roadsAndSewersBaseSummary.concAttenuation, ...flattenCutFillsExtraItemsToBoqLines(state.concAttenuationExtraItems)],
          boxCulverts: [...roadsAndSewersBaseSummary.boxCulverts, ...flattenCutFillsExtraItemsToBoqLines(state.boxCulvertsExtraItems)],
          roads: [...roadsAndSewersBaseSummary.roads, ...flattenCutFillsExtraItemsToBoqLines(state.roadsExtraItems)],
          mainServices: [...roadsAndSewersBaseSummary.mainServices, ...flattenCutFillsExtraItemsToBoqLines(state.mainServicesExtraItems)],
          substation: [...roadsAndSewersBaseSummary.substation, ...flattenCutFillsExtraItemsToBoqLines(state.substationExtraItems)],
          pos: [...roadsAndSewersBaseSummary.pos, ...flattenCutFillsExtraItemsToBoqLines(state.posExtraItems)],
          compound: [...roadsAndSewersBaseSummary.compound, ...flattenCutFillsExtraItemsToBoqLines(state.compoundExtraItems)],
          // Each Roads & Sewers Dynamic Bill Summary tab (useDynamicBillSummary.tsx /
          // ReviewCalculate's "+ Add Tab" control) gets its own summary key named
          // after the tab, same flat "key holds this domain's line-item array" shape
          // every fixed roadAndSewers.summary domain key already uses.
          ...serializeDynamicBillSummaryTabs(state.roadsAndSewersDynamicBillSummaryTabs ?? []),
        }
      : undefined;
    return [
      {
        roadAndSewers: roadsAndSewersGrandTotal !== undefined
          ? { totals: { grandTotal: roadsAndSewersGrandTotal }, summary: roadsAndSewersSummary }
          : {},
        groundworks: {
          totals: {
            grandTotal: groundworksGrandTotal,
          },
          ohp: groundworksTotals.ohpPercent ?? settings?.groundworks?.overheadAndProfit ?? 0,
          preliminaries: groundworksTotals.preliminariesPercent ?? settings?.groundworks?.preliminaries ?? 0,
          // Each Dynamic Bill Summary tab (see useDynamicBillSummary.tsx /
          // GroundworksReview's "+ Add Tab" control) gets its own summary key
          // named after the tab — same flat "key holds this domain's data"
          // shape every fixed summary key (cutFills, plotDrives, ...) uses,
          // e.g. a tab named "Others" persists as summary.others.
          summary: {
            ...groundworksSummary,
            // Ad-hoc Cut & Fill / Foundations / Drainage extra items
            // (useBillSummaryExtraItems.tsx) merge straight into their own
            // computed array — see flattenCutFillsExtraItemsToBoqLines above
            // (a generic helper despite the name, reused for any
            // BoqSummaryLineItem-shaped fixed summary's own extra-items
            // bucket) and flattenDrainageExtraItemsToBoqLines (Drainage's
            // own summary line shape uses string quantity/rate/amount).
            cutFills: [
              ...groundworksSummary.cutFills,
              ...flattenCutFillsExtraItemsToBoqLines(state.groundworks?.cutFillsExtraItems),
            ],
            foundations: [
              ...groundworksSummary.foundations,
              ...flattenCutFillsExtraItemsToBoqLines(state.groundworks?.foundationsExtraItems),
            ],
            plotDrives: [
              ...groundworksSummary.plotDrives,
              ...flattenCutFillsExtraItemsToBoqLines(state.groundworks?.plotDrivesExtraItems),
            ],
            plotService: [
              ...groundworksSummary.plotService,
              ...flattenCutFillsExtraItemsToBoqLines(state.groundworks?.plotServiceExtraItems),
            ],
            drainage: [
              ...groundworksSummary.drainage,
              ...flattenDrainageExtraItemsToBoqLines(state.groundworks?.drainageExtraItems),
            ],
            scaffold: [
              ...groundworksSummary.scaffold,
              ...flattenCutFillsExtraItemsToBoqLines(state.groundworks?.scaffoldExtraItems),
            ],
            plotPavings: [
              ...groundworksSummary.plotPavings,
              ...flattenCutFillsExtraItemsToBoqLines(state.groundworks?.plotPavingsExtraItems),
            ],
            garden: [
              ...groundworksSummary.garden,
              ...flattenCutFillsExtraItemsToBoqLines(state.groundworks?.gardenExtraItems),
            ],
            retainingWalls: [
              ...groundworksSummary.retainingWalls,
              ...flattenCutFillsExtraItemsToBoqLines(state.groundworks?.retainingWallsExtraItems),
            ],
            geoWalls: [
              ...groundworksSummary.geoWalls,
              ...flattenCutFillsExtraItemsToBoqLines(state.groundworks?.geoWallsExtraItems),
            ],
            ...serializeDynamicBillSummaryTabs(state.groundworks?.dynamicBillSummaryTabs ?? []),
          },
        },
      },
    ];
  })();

  // Build the complete payload
  const payload = {
    ...payloadData,
    ...(effectiveStatus && { status: effectiveStatus, boqStatus: effectiveStatus }),
    lastModified: new Date().toISOString(),
    lastModifiedBy: userName || "Unknown",
    ...(opportunityNo && { opportunityNo }),
    billOfQuantities,
    globalSettings: {
      ...payloadData.globalSettings,
      groundworks: {
        ...payloadData.globalSettings?.groundworks,
        overheadAndProfit: groundworksTotals.ohpPercent ?? settings?.groundworks?.overheadAndProfit ?? 0,
        preliminaries: groundworksTotals.preliminariesPercent ?? settings?.groundworks?.preliminaries ?? 0,
        discount: groundworksTotals.discountPercent ?? settings?.groundworks?.discount ?? 0,
        discountAdded: (groundworksTotals.discountAmount != null
          ? groundworksTotals.discountAmount > 0
          : includeDiscountDisplay) ? "true" : "false",
      },
    },
    // Always the sum of billOfQuantities[0].groundworks.totals.grandTotal and
    // .roadAndSewers.totals.grandTotal — see calculateBoqTenderTotal (src/utils/boqTotals.ts).
    totals: totalsData || {
      tenderTotal: calculateBoqTenderTotal(billOfQuantities?.[0]),
    },
    groundworksItemLibrary: buildGroundworksItemLibrary(itemLibraryData, {
      cutFills: state.groundworks?.cutFillsTable,
      foundations: state.groundworks?.foundationsTable,
      drainage: state.groundworks?.drainageTableStandard,
      plotDrives: state.groundworks?.plotDrivesTable,
      garden: state.groundworks?.gardenTable,
      plotPavings: state.groundworks?.plotPavingsTable,
      scaffold: state.groundworks?.scaffoldTable,
      plotService: state.groundworks?.plotServiceTable,
      retainingWalls: state.groundworks?.retainingWallsTable,
      geoToberWall: state.groundworks?.geoToberWallTable,
    }, {
      drainageDimensionValues: (state.groundworks as unknown as Record<string, unknown>)?.drainageDimensionValues as Record<string, string> | undefined,
      foundationsPrefixValues: (state.groundworks as unknown as Record<string, unknown>)?.foundationsPrefixValues as Record<string, string> | undefined,
      cutFillsPrefixValues: (state.groundworks as unknown as Record<string, unknown>)?.cutFillsPrefixValues as Record<string, string> | undefined,
      plotDrivesDimensionValues: (state.groundworks as unknown as Record<string, unknown>)?.plotDrivesDimensionValues as Record<string, string> | undefined,
      plotDrivesCodeOverrides: (state.groundworks as unknown as Record<string, unknown>)?.plotDrivesCodeOverrides as Record<string, string> | undefined,
      substructureBlockType: (state.groundworks as unknown as Record<string, unknown>)?.substructureBlockType as string | undefined,
      plotServiceConfig: state.groundworks?.plotServiceConfig,
      screenWallsLabel: state.groundworks?.wallsBuildUpConfig?.screenWallsLabel,
      eoPillarLabel: state.groundworks?.wallsBuildUpConfig?.eoPillarLabel,
    }),
  };

  return payload;
}

/**
 * Creates a simplified BOQ payload for initial creation
 * Used when saving BOQ data with basic information
 */
export function createSimpleBoqPayload(options: {
  state: TenderState;
  userName?: string;
  opportunityNo?: string;
  billOfQuantitiesData?: any;
  totalsData?: any;
  itemLibraryData?: ItemLibraryResponse;
}) {
  const { state, userName, opportunityNo, billOfQuantitiesData, totalsData, itemLibraryData } = options;

  const payloadData = prepareTenderForAPI(state, userName);

  return {
    ...payloadData,
    lastModified: new Date().toISOString(),
    ...(opportunityNo && { opportunityNo }),
    ...(billOfQuantitiesData && { billOfQuantities: billOfQuantitiesData }),
    // Always the sum of billOfQuantities[0].groundworks.totals.grandTotal and
    // .roadAndSewers.totals.grandTotal — see calculateBoqTenderTotal (src/utils/boqTotals.ts).
    ...((totalsData || billOfQuantitiesData) && {
      totals: totalsData || { tenderTotal: calculateBoqTenderTotal(billOfQuantitiesData?.[0]) },
    }),
    groundworksItemLibrary: buildGroundworksItemLibrary(itemLibraryData, {
      cutFills: state.groundworks?.cutFillsTable,
      foundations: state.groundworks?.foundationsTable,
      drainage: state.groundworks?.drainageTableStandard,
      plotDrives: state.groundworks?.plotDrivesTable,
      garden: state.groundworks?.gardenTable,
      plotPavings: state.groundworks?.plotPavingsTable,
      scaffold: state.groundworks?.scaffoldTable,
      plotService: state.groundworks?.plotServiceTable,
      retainingWalls: state.groundworks?.retainingWallsTable,
      geoToberWall: state.groundworks?.geoToberWallTable,
    }, {
      drainageDimensionValues: (state.groundworks as unknown as Record<string, unknown>)?.drainageDimensionValues as Record<string, string> | undefined,
      foundationsPrefixValues: (state.groundworks as unknown as Record<string, unknown>)?.foundationsPrefixValues as Record<string, string> | undefined,
      cutFillsPrefixValues: (state.groundworks as unknown as Record<string, unknown>)?.cutFillsPrefixValues as Record<string, string> | undefined,
      plotDrivesDimensionValues: (state.groundworks as unknown as Record<string, unknown>)?.plotDrivesDimensionValues as Record<string, string> | undefined,
      plotDrivesCodeOverrides: (state.groundworks as unknown as Record<string, unknown>)?.plotDrivesCodeOverrides as Record<string, string> | undefined,
      substructureBlockType: (state.groundworks as unknown as Record<string, unknown>)?.substructureBlockType as string | undefined,
      plotServiceConfig: state.groundworks?.plotServiceConfig,
      screenWallsLabel: state.groundworks?.wallsBuildUpConfig?.screenWallsLabel,
      eoPillarLabel: state.groundworks?.wallsBuildUpConfig?.eoPillarLabel,
    }),
  };
}

/**
 * Creates a version payload for BOQ versioning
 * Used when creating or updating BOQ versions in the version history
 */
export function createVersionPayload(options: VersionPayloadOptions) {
  const { projectName, boqPayload, changeLog, version } = options;

  return {
    projectName,
    boq: boqPayload,
    changeLog,
    ...(version && { version }),
  };
}

/**
 * Creates a mutation payload for BOQ creation/update hooks
 * Wraps the BOQ payload with additional metadata for the mutation
 */
export function createMutationPayload(options: {
  boqPayload: any;
  statusChanged?: boolean;
}) {
  const { boqPayload, statusChanged = false } = options;

  return {
    data: boqPayload,
    statusChanged,
  };
}

/**
 * Fetches retaining walls build-up specs from API when wallsBuildUpDimensions
 * is not in state (e.g. loaded projects). Returns undefined if state has dimensions.
 */
export async function fetchRetainingWallsBuildUpSpecs(
  projectName: string,
  wallsBuildUpDimensions: Record<string, unknown>,
): Promise<RetainingWallsBuildUpSpecification[] | undefined> {
  if (Object.keys(wallsBuildUpDimensions).length > 0) return undefined;
  try {
    const boqVersionRaw = localStorage.getItem("currentBoqVersion");
    // version may be stored as "GWKS-3"/"RS-3" (set by /version-history) — must
    // extract the plain numeric part first, otherwise `currentVersion > 0`
    // compares a string like "GWKS-3" against 0 (always false via NaN), so
    // this silently never fetches and retaining-wall specs go missing.
    const currentVersion = boqVersionRaw ? getNumericBoqVersion(JSON.parse(boqVersionRaw)?.version ?? 0) : 0;
    if (currentVersion > 0 && projectName) {
      const buildUp = await retainingWallsBuildUpApi.get(projectName, currentVersion);
      return buildUp?.specifications ?? undefined;
    }
  } catch {
    // Non-blocking — missing specs means fallback to wallsBuildUpDimensions
  }
  return undefined;
}

/**
 * Fetches geo/tober wall build-up specs from API when geoToberWallBuildUpDimensions
 * is not in state (e.g. loaded projects). Returns undefined if state has dimensions.
 */
export async function fetchGeoToberWallsBuildUpSpecs(
  projectName: string,
  _geoToberWallBuildUpDimensions: Record<string, unknown>,
): Promise<GeoToberWallBuildUpSpecification[] | undefined> {
  // Always fetch from API so Tobermore specs (which may not be in state dimensions)
  // are included alongside any geowall specs.
  try {
    const boqVersionRaw = localStorage.getItem("currentBoqVersion");
    // version may be stored as "GWKS-3"/"RS-3" (set by /version-history) — must
    // extract the plain numeric part first, otherwise `currentVersion > 0`
    // compares a string like "GWKS-3" against 0 (always false via NaN), so
    // this silently never fetches and geo/tober wall specs go missing.
    const currentVersion = boqVersionRaw ? getNumericBoqVersion(JSON.parse(boqVersionRaw)?.version ?? 0) : 0;
    if (currentVersion > 0 && projectName) {
      const buildUp = await geoToberWallBuildUpApi.get(projectName, currentVersion);
      return buildUp?.specifications ?? undefined;
    }
  } catch {
    // Non-blocking — missing specs means fallback to geoToberWallBuildUpDimensions
  }
  return undefined;
}

/**
 * Helper to calculate combined tender total
 * Used when both groundworks and roads & sewers job types are selected
 */
export function calculateCombinedTotal(
  groundworksTotal: number,
  roadsAndSewersTotal: number = 0,
  jobTypes?: { groundworks?: boolean; roadsAndSewers?: boolean }
) {
  if (jobTypes?.roadsAndSewers) {
    return groundworksTotal + roadsAndSewersTotal;
  }
  return groundworksTotal;
}
